import React, { useMemo, useRef } from 'react';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { ELEMENT_TABLE } from '@udecode/plate';
import {
  Plate,
  PlateContent,
  createPlugins,
} from '@udecode/plate-core';
import clsx from 'clsx';

import { useStickyElement } from 'hooks/useStickyElement';
import { plugins } from './contexts/fullEditor';
import { Toolbar } from './components/toolbar/Toolbar';
import { createDocumentSettingsPlugin } from './plugins/document-settings/createDocumentSettingsPlugin';
import { createColorPickerSettingsPlugin } from './plugins/color-picker-settings/createColorPickerSettingsPlugin';
import ErrorModal from './components/popup/Error/ErrorModal';

import './plate.scss';

/**
 * React Error Boundary for the PlateEditor.
 *
 * React only supports class components as error boundaries. This catches errors
 * that happen during rendering (e.g. toolbar reads getMarks() on a stale
 * selection path) and prevents the whole page from unmounting.
 *
 * Slate's document state lives in the editor object (a mutable ref), not in
 * React state, so it is preserved across error boundary resets. The user can
 * dismiss the banner and continue editing.
 *
 * When hasError is true we still render {children} — this gives React another
 * chance to render the subtree on the next cycle. If the error was transient
 * (e.g. a momentarily invalid selection path) it will self-heal. If it is
 * persistent, the banner stays visible and the user can dismiss to retry.
 */
class PlateEditorErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    /* eslint-disable-next-line react/no-unused-state */
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, info) {
    if (window.Rollbar) {
      window.Rollbar.error('PlateEditor render error', error, info);
    }
  }

  /* eslint-disable-next-line react/no-unused-class-component-methods */
  dismiss = () => {
    /* eslint-disable-next-line react/no-unused-state */
    this.setState({ hasError: false });
  };

  render() {
    const { children } = this.props;
    return (
      <>
        {children}
      </>
    );
  }
}

const editableProps = {
  placeholder: 'Type...',
  style: { minHeight: '100px', outline: 'none' },
};

const PlateEditor = ({
  rootElement, // React dnd will error with setting up multiple html5 backends, so root element needs to be provided.
  initialValue,
  onChange,
  stickyProps,
  toolbarProps,
  documentSettings,
}) => {
  const editorRef = useRef(null);
  const { offset } = stickyProps || {};
  const { colorPickerProps } = toolbarProps || {};
  const { elementRef, isSticky } = stickyProps ? useStickyElement(false, offset) : {};
  const { width } = documentSettings || {};

  const addtPlugins = useMemo(
    () => createPlugins([
      createColorPickerSettingsPlugin({
        options: colorPickerProps,
      }),
      createDocumentSettingsPlugin(
        { options: documentSettings },
        { [ELEMENT_TABLE]: { minColumnWidth: width } },
      ),
    ]),
    [colorPickerProps, documentSettings],
  );

  // We don't want to call handle change when user changes the selection by clicking into the editor
  // or other related events.
  const handleChange = (value => {
    if (editorRef.current) {
      const isAstChange = editorRef.current.operations.some(op => op.type !== 'set_selection');
      if (!isAstChange) {
        return;
      }
    }
    onChange(value);
  });

  // The core editor protocol plugin reset marks when the selection is at the
  // start of a block and after delete operations. We don't want that behavior
  // for our editor so that users can retain font family when backspacing.
  const disableCorePlugins = {
    editorProtocol: true,
  };

  return (
    <PlateEditorErrorBoundary>
      <div className="ap-slate-editor">
        <DndProvider
          backend={HTML5Backend}
          options={{ rootElement }}
        >
          <Plate
            editorRef={editorRef}
            initialValue={initialValue}
            normalizeInitialValue
            plugins={[...plugins, ...addtPlugins]}
            onChange={handleChange}
            disableCorePlugins={disableCorePlugins}
          >
            <div
              ref={elementRef}
              className={clsx('plate-toolbar__wrapper', isSticky && 'has-detached-element')}
            >
              <Toolbar
                isSticky={isSticky}
                toolbarProps={toolbarProps}
              />
            </div>
            <PlateContent
              {...editableProps}
            />
            <ErrorModal/>
          </Plate>
        </DndProvider>
      </div>
    </PlateEditorErrorBoundary>
  );
};

export default PlateEditor;
